<template>
  <div class="mobile-home" :class="{ 'dark-theme': isDark }">
    <!-- 头部区域 -->
    <header class="mobile-header">
      <div class="logo-section">
        <img src="/assets/shenxiaozhi.png" alt="神小智" class="logo">
        <div class="brand-info">
          <h1>神小智AI</h1>
          <p>全球领先的AI创意视频平台</p>
        </div>
      </div>
      
      <!-- 主题切换按钮 -->
      <div class="theme-toggle" @click="toggleTheme">
        <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
      </div>
    </header>

    <!-- 主要功能区域 -->
    <main class="main-content">
      <!-- AI视频生成按钮 -->
      <div class="feature-section">
        <div class="main-feature-card">
          <div class="feature-icon">
            <i class="fas fa-video"></i>
          </div>
          <div class="feature-content">
            <h2>神小智AI生成视频</h2>
            <p>一键生成专业级AI创意视频<br>支持多种风格和场景</p>
          </div>
          <button @click="goToVideoCreate" class="feature-btn">
            立即体验
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- 功能菜单网格 -->
      <div class="menu-grid">
        <div class="menu-item" @click="handleAuthAction">
          <div class="menu-icon">
            <i :class="isLoggedIn ? 'fas fa-user' : 'fas fa-sign-in-alt'"></i>
          </div>
          <span>{{ isLoggedIn ? '用户中心' : '登录/注册' }}</span>
        </div>
        
        <div class="menu-item" @click="goToPackageCenter">
          <div class="menu-icon">
            <i class="fas fa-shopping-bag"></i>
          </div>
          <span>套餐中心</span>
        </div>
        
        <div class="menu-item" @click="openCustomerService">
          <div class="menu-icon">
            <i class="fas fa-headset"></i>
          </div>
          <span>客服咨询</span>
        </div>
        
        <div class="menu-item" @click="handleFeedback">
          <div class="menu-icon">
            <i class="fas fa-comment-dots"></i>
          </div>
          <span>问题反馈</span>
        </div>
      </div>

      <!-- 特色功能介绍 -->
      <div class="features-showcase">
        <h3>AI能力矩阵</h3>
        <div class="features-list">
          <div class="feature-item" v-for="feature in features" :key="feature.title">
            <div class="feature-item-icon">
              <i :class="feature.icon"></i>
            </div>
            <div class="feature-item-content">
              <h4>{{ feature.title }}</h4>
              <p>{{ feature.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 底部版权信息 -->
    <footer class="mobile-footer">
      <p>© 2025 神小智AI创意视频平台 版权所有</p>
    </footer>

    <!-- 客服二维码弹窗 -->
    <div v-if="showQRCode" class="qr-modal" @click="showQRCode = false">
      <div class="qr-content" @click.stop>
        <h3>客服咨询</h3>
        <img src="/assets/二维码.jpg" alt="客服二维码" class="qr-image">
        <p>扫码添加客服微信</p>
        <button @click="showQRCode = false">关闭</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from '@/utils/router.js';
import { useUserStoreHook } from '@/store/modules/user';
import { useTheme } from '@/composables/useTheme';

const router = useRouter();
const userStore = useUserStoreHook();
const showQRCode = ref(false);

// 主题管理
const { isDark, loadTheme, toggleTheme } = useTheme();

// 页面初始化
onMounted(() => {
  loadTheme();
});

// 检查用户是否已登录
const isLoggedIn = computed(() => !!userStore.token);

// 功能特色列表
const features = ref([
  {
    title: '视频生成',
    description: '智能AI视频创作，支持多种风格',
    icon: 'fas fa-video'
  },
  {
    title: '图片生成',
    description: 'AI图片创作，高质量输出',
    icon: 'fas fa-image'
  },
  {
    title: '语音合成',
    description: '自然语音生成，多语种支持',
    icon: 'fas fa-microphone'
  },
  {
    title: '智能剪辑',
    description: '自动化视频剪辑，专业效果',
    icon: 'fas fa-cut'
  }
]);

// 导航方法
const goToVideoCreate = () => {
  console.log('点击立即体验按钮，准备跳转到video-create页面');
  console.log('用户登录状态:', isLoggedIn.value);
  if (isLoggedIn.value) {
    console.log('用户已登录，直接跳转到video-create页面');
    router.push('/pages/mobile/video-create');
  } else {
    console.log('用户未登录，跳转到登录页面');
    router.push('/pages/mobile/login?redirect=' + encodeURIComponent('/pages/mobile/video-create'));
  }
};

// 处理登录/用户中心按钮点击
const handleAuthAction = () => {
  if (isLoggedIn.value) {
    router.push('/pages/mobile/user-center');
  } else {
    router.push('/pages/mobile/login');
  }
};

// 问题反馈
const handleFeedback = () => {
  // 跳转到飞书反馈表单，与PC端保持一致
  window.open('https://magicpen.feishu.cn/share/base/form/shrcnOrqAI7anRrwi33o0ZUZDMb', '_blank');
};

const goToPackageCenter = () => {
  console.log('点击套餐中心按钮，准备跳转到package-center页面');
  console.log('用户登录状态:', isLoggedIn.value);
  if (isLoggedIn.value) {
    console.log('用户已登录，直接跳转到package-center页面');
    router.push('/pages/mobile/package-center');
  } else {
    console.log('用户未登录，跳转到登录页面');
    router.push('/pages/mobile/login?redirect=' + encodeURIComponent('/pages/mobile/package-center'));
  }
};

const openCustomerService = () => {
  showQRCode.value = true;
};

</script>

<style scoped lang="scss">
.mobile-home {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow-x: hidden;
}

.mobile-header {
  padding: 20px 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  
  .logo {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    object-fit: cover;
  }
  
  .brand-info {
    h1 {
      color: white;
      font-size: 24px;
      font-weight: bold;
      margin: 0;
    }
    
    p {
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
      margin: 2px 0 0 0;
    }
  }
}

.main-content {
  padding: 20px 16px;
}

.feature-section {
  margin-bottom: 30px;
}

.main-feature-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
  
  .feature-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 16px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    i {
      font-size: 36px;
      color: white;
    }
  }
  
  .feature-content {
    margin-bottom: 20px;
    
    h2 {
      color: #333;
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #666;
      font-size: 16px;
      line-height: 1.5;
      margin: 0;
    }
  }
  
  .feature-btn {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 16px 32px;
    border-radius: 50px;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 30px;
}

.menu-item {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.95);
  }
  
  .menu-icon {
    width: 50px;
    height: 50px;
    margin: 0 auto 12px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    i {
      font-size: 24px;
      color: white;
    }
  }
  
  span {
    color: #333;
    font-size: 16px;
    font-weight: 500;
  }
}

.features-showcase {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(10px);
  margin-bottom: 20px;
  
  h3 {
    color: #333;
    font-size: 20px;
    font-weight: bold;
    margin: 0 0 20px 0;
    text-align: center;
  }
}

.features-list {
  display: grid;
  gap: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  
  .feature-item-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    i {
      font-size: 18px;
      color: white;
    }
  }
  
  .feature-item-content {
    h4 {
      color: #333;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 4px 0;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 0;
      line-height: 1.4;
    }
  }
}

.mobile-footer {
  padding: 20px 16px;
  text-align: center;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    margin: 0;
  }
}

.qr-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.qr-content {
  background: white;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 300px;
  width: 100%;
  
  h3 {
    color: #333;
    font-size: 20px;
    margin: 0 0 20px 0;
  }
  
  .qr-image {
    max-width: 280px;
    width: 90%;
    height: auto;
    border-radius: 12px;
    margin-bottom: 16px;
    object-fit: contain;
  }
  
  p {
    color: #666;
    margin: 0 0 20px 0;
  }
  
  button {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .main-feature-card {
    padding: 20px;
    
    .feature-icon {
      width: 60px;
      height: 60px;
      
      i {
        font-size: 28px;
      }
    }
    
    .feature-content h2 {
      font-size: 20px;
    }
    
    .feature-btn {
      padding: 14px 28px;
      font-size: 16px;
    }
  }
  
  .menu-grid {
    gap: 12px;
  }
  
  .menu-item {
    padding: 16px;
    
    .menu-icon {
      width: 40px;
      height: 40px;
      
      i {
        font-size: 20px;
      }
    }
    
    span {
      font-size: 14px;
    }
  }
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.main-feature-card,
.menu-item,
.features-showcase {
  animation: fadeInUp 0.6s ease-out;
}

.menu-item:nth-child(2) { animation-delay: 0.1s; }
.menu-item:nth-child(3) { animation-delay: 0.2s; }
.menu-item:nth-child(4) { animation-delay: 0.3s; }

// 主题切换按钮
.theme-toggle {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  i {
    font-size: 20px;
    color: white;
    transition: all 0.3s ease;
  }
}

// 暗色主题样式
.mobile-home.dark-theme {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);

  .mobile-header {
    background: rgba(0, 0, 0, 0.3);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }


  .theme-toggle {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .main-feature-card {
    background: rgba(30, 30, 50, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    &::before {
      background: rgba(255, 255, 255, 0.1);
    }

    .feature-icon {
      background: linear-gradient(135deg, #2d3748, #4a5568);
      border: 1px solid rgba(255, 255, 255, 0.2);
      
      i {
        color: #e2e8f0;
      }
    }

    .feature-content {
      h2 {
        color: #ffffff;
      }

      p {
        color: #ffffff;
      }
    }

    .feature-btn {
      background: linear-gradient(135deg, #2d3748, #4a5568);
      box-shadow: 0 8px 20px rgba(45, 55, 72, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #ffffff;

      &:hover {
        background: linear-gradient(135deg, #4a5568, #2d3748);
        box-shadow: 0 12px 30px rgba(74, 85, 104, 0.5);
        transform: translateY(-3px);
      }

      &:active {
        transform: translateY(-1px);
      }
    }
  }

  .menu-item {
    background: rgba(30, 30, 50, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(45, 55, 72, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    .menu-icon {
      background: linear-gradient(135deg, #2d3748, #4a5568);
      border: 1px solid rgba(255, 255, 255, 0.2);
      
      i {
        color: #e2e8f0;
      }
    }

    span {
      color: #e2e8f0;
      font-weight: 500;
    }
  }

  .features-showcase {
    background: rgba(30, 30, 50, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.1);

    h3 {
      color: #ffffff;
    }
  }

  .feature-item {
    background: rgba(40, 40, 60, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.05);

    .feature-item-icon {
      background: linear-gradient(135deg, #4a90e2, #7b68ee);
    }

    .feature-item-content {
      h4 {
        color: #ffffff;
      }

      p {
        color: #ffffff;
      }
    }
  }

  .mobile-footer {
    p {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .qr-modal {
    .qr-content {
      background: rgba(30, 30, 50, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.1);

      h3 {
        color: #ffffff;
      }

      p {
        color: #ffffff;
      }

      button {
        background: linear-gradient(135deg, #2d3748, #4a5568);
        color: #e2e8f0;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        padding: 12px 24px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          background: linear-gradient(135deg, #4a5568, #2d3748);
          border: 1px solid rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }
}
</style>
